import { AxiosError } from 'axios'
import _ from 'lodash'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useLocation } from 'react-router-dom'

import { IPhysicianAutoSuggestResultProps } from './PhysicianAutoSuggest.model'
import {
  StyledPhysicianAutoSuggestResultItem,
  StyledPhysicianAutoSuggestWrapper,
  StyledPhysicianAutoSuggestLink
} from './PhysicianAutoSuggest.styles'
import { ClinTheme } from '../../../ClinTheme'
import { AnnounceMode } from '../../../components/ClinAnnounceBar/ClinAnnounceBar'
import { ClinButton } from '../../../components/ClinButton'
import { SortDirectionType } from '../../../components/ClinTableOrderToggle/ClinTableOrderToggle'
import { ClinText } from '../../../components/ClinText'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import { useAppContext } from '../../../context/app'
import { createAnnounceEvent } from '../../../events/AnnounceEvent'
import i18n from '../../../i18n/config'
import { AnalyticsEvent } from '../../../services/Analytics'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import {
  AuthError,
  cancelGetPhysicianSearch,
  getPhysicianSearch,
  getNotEnrolledPhysicians,
  cancelGetNotEnrolledPhysicians
} from '../../../services/ApiService'
import {
  PhysiciansSummaryDto,
  PhysiciansSummarySearchDto
} from '../../../types/swaggerTypes'
export interface ISearchStylesProps {
  /** Show suggestions below search */
  showSuggestions?: boolean
}
const defaultSearchParams: PhysiciansSummarySearchDto = {
  query: '',
  filter: {
    programs: [],
    patientStatuses: []
  },
  pagination: {
    skip: 0,
    take: 5
  },
  sorting: {
    sortBy: '',
    order: SortDirectionType.Ascending
  }
}

interface IPhysicianAutoSuggestContainerProps {
  /** Amount of debounce time in milliseconds */
  debounceTimeMs?: number
  /** Number of results to display */
  maxResultsToDisplay?: number
  /** Search component to pass through */
  searchComponent(props: IPhysicianAutoSuggestResultProps): React.ReactNode
  /** Call back to broadcast onChange */
  handleOnChange?: (query: string) => void
  /** Call back to broadcast onClick */
  handleOnSelect: (suggestion: PhysiciansSummaryDto) => void
  /** Call back request to add a new physician */
  handleRequestToAddPhysician: () => void
  /** Flag to determine whether to search for all physicians or only not enrolled ones */
  searchNotEnrolled?: boolean
  /** Program ID for searching not enrolled physicians */
  programId?: string
}

export const PhysicianAutoSuggest: FunctionComponent<
  IPhysicianAutoSuggestContainerProps
> = ({
  searchComponent,
  debounceTimeMs = 500,
  handleOnChange,
  handleOnSelect,
  maxResultsToDisplay = 5,
  handleRequestToAddPhysician,
  searchNotEnrolled = false,
  programId
}: IPhysicianAutoSuggestContainerProps) => {
  const { t } = i18n
  const { dispatch } = useAppContext()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false)
  const [query, setQuery] = useState<string | undefined>()
  const [suggestion, setSuggestion] = useState<
    PhysiciansSummaryDto | undefined
  >()
  const [suggestions, setSuggestions] = useState<PhysiciansSummaryDto[]>([])
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const resetAll = () => {
    setSuggestion(undefined)
    setSuggestions([])
    setQuery('')
    setHighlightedIndex(0)
    setIsLoading(false)
    setShowSuggestions(false)
  }

  // Debounced call to API to request suggestions
  const requestSuggestions = useCallback(
    (queryStr: string) => {
      if (!queryStr || queryStr === '') {
        resetAll()
        return
      }
      setIsLoading(true)

      if (searchNotEnrolled) {
        cancelGetNotEnrolledPhysicians()
      } else {
        cancelGetPhysicianSearch()
      }

      analyticsServiceSingleton.trackEvent(AnalyticsEvent.SubmitSearchQuery, {
        query: queryStr,
        searchLocation: 'physicianAutosuggest',
        searchAPI: searchNotEnrolled ? 'notEnrolled' : 'suggest'
      })

      const searchPromise = searchNotEnrolled
        ? getNotEnrolledPhysicians(programId!, queryStr, 0, maxResultsToDisplay)
        : getPhysicianSearch({ ...defaultSearchParams, query: queryStr })

      searchPromise
        .then((results) => {
          setIsLoading(false)
          setHighlightedIndex(-1)
          setSuggestions(results.data.result.slice(0, maxResultsToDisplay))
          setShowSuggestions(
            results.data.result.length > 0 && queryStr.length > 0
          )
          if (queryStr && queryStr !== '') {
            analyticsServiceSingleton.trackEvent(
              AnalyticsEvent.ResultsForSearchQuery,
              { query: queryStr, numberResults: results.data.result.length }
            )
          }
        })
        .catch((error: AxiosError) => {
          // If request is cancelled continue
          if (error.message === AuthError.RequestCancelled) {
            return
          }
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching suggestions. ${error.message} ${
                error.code ? error.code : ''
              }`
            )
          )
          setShowSuggestions(false)
        })
    },
    [dispatch, maxResultsToDisplay, searchNotEnrolled, programId]
  )

  // Debounced call to API to request suggestions
  const debounceEventHandler = useMemo(
    () =>
      _.debounce(
        (searchString: string) => requestSuggestions(searchString),
        debounceTimeMs
      ),
    [requestSuggestions, debounceTimeMs]
  )

  // Method to provide the rendering cell for returned suggestions
  const renderSuggestions = (
    items: PhysiciansSummaryDto[]
  ): React.ReactNode | React.ReactNode[] => {
    const { t } = i18n
    let results
    if (items && items.length === 0 && !isLoading) {
      return (
        <>
          <StyledPhysicianAutoSuggestResultItem
            tabIndex={0}
            key={'no-results-title'}
            style={{
              color: ClinTheme.colors.black,
              fontSize: ClinTheme.fontSizes[2]
            }}
          >
            {t('enrol_physician_on_to_program:no_matches_found')}
          </StyledPhysicianAutoSuggestResultItem>
          <StyledPhysicianAutoSuggestLink
            tabIndex={0}
            key={'no-results'}
            selected={highlightedIndex === suggestions.length}
            style={{
              fontWeight: ClinTheme.fontWeights.medium,
              fontSize: ClinTheme.fontSizes[2],
              color: ClinTheme.colors.primaryLight
            }}
            onClick={() => handleRequestToAddPhysician()}
          >
            {t('enrol_physician_on_to_program:physician_not_listed')}
          </StyledPhysicianAutoSuggestLink>
        </>
      )
    }
    if (items && items.length > 0) {
      results = items.map((suggestion, index) => {
        return (
          <StyledPhysicianAutoSuggestResultItem
            key={`${suggestion.physicianFullName}-${index}`}
            tabIndex={0}
            selected={highlightedIndex === index}
            onClick={() => handleSelect(index)}
          >
            <ClinText
              wordBreak="break-all"
              variant={TypographyVariant.LargeParagraph}
              fontWeight={ClinTheme.fontWeights.medium}
            >
              {`${suggestion.physicianTitle ?? ''} ${
                suggestion.physicianFirstName
              } ${suggestion.physicianLastName}`}
            </ClinText>
            <ClinText
              wordBreak="break-all"
              variant={TypographyVariant.LargeParagraph}
            >
              {suggestion.physicianEmail}
            </ClinText>
          </StyledPhysicianAutoSuggestResultItem>
        )
      })
      results.push(
        <StyledPhysicianAutoSuggestLink
          tabIndex={0}
          key={'no-results'}
          selected={highlightedIndex === suggestions.length}
          style={{
            fontWeight: ClinTheme.fontWeights.medium,
            fontSize: ClinTheme.fontSizes[2],
            color: ClinTheme.colors.primaryLight
          }}
          onClick={() => handleRequestToAddPhysician()}
        >
          {t('enrol_physician_on_to_program:physician_not_listed')}
        </StyledPhysicianAutoSuggestLink>
      )
    }
    return results
  }

  const handleEscapeKey = () => {
    resetAll()
  }

  const handleDownOrTabKey = () => {
    const INITIAL_IDX = 0
    const idx = highlightedIndex
    const nextIdx = idx !== undefined ? idx + 1 : INITIAL_IDX

    if (nextIdx < suggestions.length) {
      setHighlightedIndex(nextIdx)
    } else {
      setHighlightedIndex(INITIAL_IDX)
    }
  }

  const handleUpOrShiftTabKey = () => {
    const lastIdx = suggestions.length - 1
    const idx = highlightedIndex
    const prevIdx = idx !== undefined ? idx - 1 : lastIdx

    if (prevIdx >= 0) {
      setHighlightedIndex(prevIdx)
    } else {
      setHighlightedIndex(lastIdx)
    }
  }

  const handleEnterKey = () => {
    if (highlightedIndex >= 0) {
      handleSelect && handleSelect(highlightedIndex)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const UP = 'ArrowUp'
    const DOWN = 'ArrowDown'
    const ENTER = 'Enter'
    const ESCAPE = 'Escape'
    const TAB = 'Tab'

    switch (e.key) {
      case ESCAPE:
        handleEscapeKey()
        break
      case DOWN:
      case TAB:
        handleDownOrTabKey()
        break
      case UP:
        handleUpOrShiftTabKey()
        break
      case ENTER:
        handleEnterKey()
        break
      default:
        // Handle shift+tab as a special case
        if (e.shiftKey && e.key === TAB) {
          handleUpOrShiftTabKey()
        }
        break
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const queryStr = e.target.value
    setQuery(queryStr)
    debounceEventHandler(queryStr)
    handleOnChange && handleOnChange(queryStr)
  }

  const handleSubmitPhysician = () => {
    suggestion && handleOnSelect(suggestion)
    setQuery('')
    setSuggestion(undefined)
  }

  const handleSelect = (selectedIndex: number) => {
    if (suggestions[selectedIndex]) {
      setQuery(
        `${suggestions[selectedIndex].physicianTitle ?? ''} ${
          suggestions[selectedIndex].physicianFirstName
        } ${suggestions[selectedIndex].physicianLastName}`
      )
      setShowSuggestions(false)
      setSuggestion(suggestions[selectedIndex])
    }
  }

  // Detect outside click
  const parentNode = useRef<HTMLDivElement>(null)
  const handleClickedOutside = (event: MouseEvent) => {
    if (parentNode?.current?.contains(event.target as Node)) {
      // inside click
      return
    }
    // outside click - clear results
    setShowSuggestions(false)
  }

  useEffect(() => {
    document.addEventListener('click', handleClickedOutside)
    return () => document.removeEventListener('click', handleClickedOutside)
  })

  useEffect(() => {
    resetAll()
    return () => {
      if (searchNotEnrolled) {
        cancelGetNotEnrolledPhysicians()
      } else {
        cancelGetPhysicianSearch()
      }
    }
  }, [location.pathname, searchNotEnrolled])

  return (
    <StyledPhysicianAutoSuggestWrapper ref={parentNode}>
      {searchComponent({
        query,
        isLoading,
        showSuggestions: showSuggestions,
        suggestions,
        renderSuggestions,
        handleChange,
        handleKeyDown
      } as IPhysicianAutoSuggestResultProps)}
      <ClinButton
        tabIndex={0}
        onClick={() => handleSubmitPhysician()}
        variant="primary"
        disabled={!suggestion}
      >
        {t('enrol_physician_on_to_program:add_physician')}
      </ClinButton>
    </StyledPhysicianAutoSuggestWrapper>
  )
}
